import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface UseInfiniteScrollOptions {
  executeFn: () => void | Promise<void>;
  threshold?: number;
  direction?: 'vertical' | 'horizontal';
}

function useInfiniteScroll<T>({
  threshold = 100,
  executeFn,
  direction = 'vertical',
}: UseInfiniteScrollOptions) {
  const containerRef = useRef<HTMLDivElement>(null);

  // 记录上一次的滚动位置
  const lastScrollPos = useRef(0);

  const checkScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollSize, scrollPos, clientSize;
    if (direction === 'vertical') {
      scrollSize = container.scrollHeight;
      scrollPos = container.scrollTop;
      clientSize = container.clientHeight;
    } else {
      scrollSize = container.scrollWidth;
      scrollPos = container.scrollLeft;
      clientSize = container.clientWidth;
    }

    // 只有向下/向右滚动时才触发加载
    const isScrollingForward = scrollPos > lastScrollPos.current;
    lastScrollPos.current = scrollPos;

    // 判断是否滚动到底部/最右侧阈值，且是向下/向右滚动
    if (isScrollingForward && scrollSize - (scrollPos + clientSize) <= threshold) {
      executeFn();
    }
  }, [threshold, executeFn, direction]);

  // 防抖
  const debouncedCheckScroll = useMemo(() => debounce(checkScroll, 200), [checkScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', debouncedCheckScroll);
    return () => {
      container.removeEventListener('scroll', debouncedCheckScroll);
    };
  }, [debouncedCheckScroll]);

  return {
    containerRef,
  };
}

export default useInfiniteScroll;
